import apiClient from "../../../helpers/apiClient";
import uploadFile from "../../../utilities/uploadFile";

export const dataURLToFile = (dataUrl, filename) => {
    if (!dataUrl) return null;
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)?.[1] || "image/png";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n > 0) {
        n -= 1;
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
};

const composeSignatureOnWhiteBackground = (sourceCanvas) => {
    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = sourceCanvas.width;
    exportCanvas.height = sourceCanvas.height;
    const ctx = exportCanvas.getContext("2d");
    if (!ctx) {
        return sourceCanvas;
    }
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
    ctx.drawImage(sourceCanvas, 0, 0);
    return exportCanvas;
};

const getSignatureDataUrl = (canvas) => {
    if (!canvas) return "";

    if (typeof canvas.isEmpty === "function" && canvas.isEmpty()) {
        return "";
    }

    let sourceCanvas = null;
    if (typeof canvas.getCanvas === "function") {
        sourceCanvas = canvas.getCanvas();
    } else if (typeof canvas.toDataURL === "function" && typeof canvas.getContext === "function") {
        sourceCanvas = canvas;
    }

    if (!sourceCanvas) {
        return "";
    }

    const exportCanvas = composeSignatureOnWhiteBackground(sourceCanvas);
    return exportCanvas.toDataURL("image/png");
};

export async function uploadSignatureCanvas(canvas) {
    const dataUrl = getSignatureDataUrl(canvas);
    if (!dataUrl) {
        throw new Error("Signature is required");
    }

    const imageFile = dataURLToFile(dataUrl, "signature.png");
    if (!imageFile) {
        throw new Error("Could not process signature");
    }

    const url = await uploadFile(imageFile, "Signature");
    if (!url) {
        throw new Error("Failed to upload signature");
    }

    return url;
}

const isValidSignatureCanvas = (canvas) =>
    Boolean(canvas && (typeof canvas.isEmpty !== "function" || !canvas.isEmpty()));

export const hasRequiredSignatures = (signatureObjects, multipleSignatures) => {
    if (multipleSignatures) {
        return (
            isValidSignatureCanvas(signatureObjects.signature1) &&
            isValidSignatureCanvas(signatureObjects.signature2) &&
            isValidSignatureCanvas(signatureObjects.signature3)
        );
    }

    return isValidSignatureCanvas(signatureObjects.signature1);
};

export async function generateAgreementPreview(params) {
    const { data } = await apiClient.post("/api/agreement/generate", params, { responseType: "blob" });
    const blob = data instanceof Blob ? data : new Blob([data], { type: "application/pdf" });
    return window.URL.createObjectURL(blob);
}

const getPersistedAgreementUrl = (response) =>
    response.headers?.["x-agreement-doc-url"] || response.headers?.["X-Agreement-Doc-Url"] || "";

export async function generateAndStoreSignedAgreement(reservationId) {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const response = await apiClient.post(
        `/api/agreement/generate/${reservationId}`,
        { timezone },
        {
            responseType: "blob",
            params: { timezone },
        },
    );

    const persistedUrl = getPersistedAgreementUrl(response);
    if (persistedUrl) {
        return persistedUrl;
    }

    const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: "application/pdf" });
    const file = new File([blob], `agreement_${reservationId}.pdf`, { type: "application/pdf" });
    const uploadedAgreementUrl = await uploadFile(file, "Agreement");

    if (!uploadedAgreementUrl) {
        throw new Error("Failed to upload signed agreement");
    }

    await apiClient.patch(`/api/reservation/${reservationId}`, {
        agreementUrl: uploadedAgreementUrl,
        agreementDocUrl: uploadedAgreementUrl,
    });

    return uploadedAgreementUrl;
}
